import React from 'react';
import { useNavigate } from 'react-router-dom';

export const SmsOptInPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-white dark:bg-neutral-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/policies')}
            className="text-brand-teal hover:text-brand-teal-hover flex items-center mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Policies
          </button>
          <h1 className="text-4xl font-bold text-neutral-black dark:text-neutral-white mb-2">
            SMS Opt-In Policy
          </h1>
          <p className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
            Last Updated: January 9, 2025
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <div className="space-y-8 text-neutral-black dark:text-neutral-white">

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="mb-4">
                ChatHTL ("we," "us," or "our") offers SMS/text messaging services to provide you with important
                account notifications, promotional offers, and service updates related to ChatHTL ("Service"). This
                SMS Opt-In Policy describes our SMS messaging practices and your rights as a subscriber.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Consent to Receive SMS Messages</h2>
              <p className="mb-4">
                By opting in to receive SMS messages from ChatHTL, you expressly consent to receive automated text
                messages from us at the mobile phone number you provided. This consent is not a condition of purchase
                or use of the ChatHTL service.
              </p>
              <p className="mb-4">
                <strong>What You're Consenting To:</strong>
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Receiving SMS/text messages from ChatHTL about the ChatHTL service</li>
                <li>Receiving automated messages using an automatic telephone dialing system</li>
                <li>Messages sent to the mobile phone number you provided during opt-in</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Types of Messages</h2>
              <p className="mb-4">You may receive the following types of SMS messages from us:</p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Transactional Messages</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Authentication Codes:</strong> One-time passwords (OTP) for account login and verification</li>
                <li><strong>Two-Factor Authentication:</strong> Security codes for account protection</li>
                <li><strong>Account Notifications:</strong> Important updates about your account status</li>
                <li><strong>Service Updates:</strong> Critical information about service availability or changes</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Promotional Messages (Optional)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Special Offers:</strong> Promotional codes, discounts, and special pricing</li>
                <li><strong>New Features:</strong> Announcements about new ChatHTL capabilities</li>
                <li><strong>Marketing Communications:</strong> Updates about ChatHTL services</li>
              </ul>
              <p className="mb-4">
                <em>Note: You can opt out of promotional messages while still receiving transactional messages
                required for account security.</em>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Message Frequency</h2>
              <p className="mb-4">
                <strong>Transactional Messages:</strong> Message frequency varies based on your account activity.
                You may receive messages when you:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Request to sign in to your account (one OTP per login attempt)</li>
                <li>Enable two-factor authentication (one code per authentication request)</li>
                <li>Receive important account notifications (as needed)</li>
              </ul>
              <p className="mb-4">
                <strong>Promotional Messages:</strong> If you opt in to promotional messages, you may receive up to
                4 messages per month. Frequency may vary based on ongoing promotions and service updates.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Message and Data Rates</h2>
              <p className="mb-4">
                <strong>Message and data rates may apply.</strong> Standard messaging charges from your wireless
                carrier will apply to all SMS messages sent and received. The number of messages you receive will
                affect the charges you incur from your carrier.
              </p>
              <p className="mb-4">
                We do not charge for SMS messages, but your carrier's rates apply. Please check with your wireless
                carrier for details about your specific messaging plan and rates.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Supported Carriers</h2>
              <p className="mb-4">
                Our SMS service is available on the following carriers:
              </p>
              <p className="mb-4">
                AT&T, T-Mobile, Verizon Wireless, Sprint, Boost Mobile, Cricket Wireless, MetroPCS, U.S. Cellular,
                Virgin Mobile, and other major U.S. wireless carriers.
              </p>
              <p className="mb-4">
                Carriers are not liable for delayed or undelivered messages. Service may not be available on all
                carriers or in all areas.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. How to Opt-Out (STOP)</h2>
              <p className="mb-4">
                <strong>You may opt out of SMS messages at any time.</strong> To stop receiving all SMS messages
                from ChatHTL:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Text STOP:</strong> Reply with the word "STOP," "CANCEL," "UNSUBSCRIBE," or "END" to any
                message you receive from us</li>
                <li><strong>Account Settings:</strong> Log in to your ChatHTL account and update your notification
                preferences</li>
                <li><strong>Contact Support:</strong> Email{' '}
                  <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                    support@chathtl.com
                  </a>{' '}
                  with your request
                </li>
              </ul>
              <p className="mb-4">
                After you opt out, you will receive one final confirmation message from us acknowledging your
                opt-out request. You will not receive further messages unless you re-opt in.
              </p>
              <p className="mb-4">
                <strong>Important:</strong> Opting out of SMS messages may affect your ability to use certain
                features of the ChatHTL service, particularly those requiring two-factor authentication or OTP
                verification.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. How to Get Help (HELP)</h2>
              <p className="mb-4">
                If you need assistance with our SMS program:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Text HELP:</strong> Reply with the word "HELP" to any message to receive program
                information and support instructions</li>
                <li><strong>Email Support:</strong> Contact{' '}
                  <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                    support@chathtl.com
                  </a>
                </li>
                <li><strong>Customer Care:</strong> Visit our support center at{' '}
                  <a href="https://chathtl.com/support" className="text-brand-teal hover:text-brand-teal-hover">
                    chathtl.com/support
                  </a>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Privacy and Data Usage</h2>
              <p className="mb-4">
                We respect your privacy and handle your mobile phone number and message data in accordance with our{' '}
                <a href="/policies/privacy-policy" className="text-brand-teal hover:text-brand-teal-hover">
                  Privacy Policy
                </a>.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.1 Information We Collect</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Mobile phone number you provide</li>
                <li>Wireless carrier information</li>
                <li>Message delivery status and timestamps</li>
                <li>Opt-in and opt-out preferences</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.2 How We Use This Information</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>To send you requested SMS messages</li>
                <li>To verify message delivery</li>
                <li>To maintain opt-out preferences</li>
                <li>To comply with SMS regulations and carrier requirements</li>
                <li>To improve our messaging services</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.3 Information Sharing</h3>
              <p className="mb-4">
                We share your mobile phone number only with:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>SMS Service Provider:</strong> Azure Communication Services (Microsoft Azure) for
                message delivery</li>
                <li><strong>Your Wireless Carrier:</strong> For message transmission</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="mb-4">
                <strong>We do not sell, rent, or share your mobile phone number for third-party marketing purposes.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Mobile Number Updates</h2>
              <p className="mb-4">
                It is your responsibility to notify us if your mobile phone number changes. You can update your
                number by:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Logging in to your ChatHTL account and updating your profile</li>
                <li>Contacting support at{' '}
                  <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                    support@chathtl.com
                  </a>
                </li>
              </ul>
              <p className="mb-4">
                If you do not update your number, you may not receive important account messages and may continue
                to be charged by your carrier for messages to your old number.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Service Availability and Limitations</h2>
              <p className="mb-4">
                While we strive to provide reliable SMS services, the following limitations apply:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Carrier Limitations:</strong> Service availability depends on your wireless carrier
                and may not be available in all areas</li>
                <li><strong>Delivery Delays:</strong> Messages may be delayed due to carrier issues, network
                congestion, or other factors beyond our control</li>
                <li><strong>Failed Delivery:</strong> We are not responsible for messages that fail to deliver
                due to carrier issues, phone settings, or other technical problems</li>
                <li><strong>Service Interruptions:</strong> Service may be temporarily unavailable due to
                maintenance, updates, or technical issues</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Age Restrictions</h2>
              <p className="mb-4">
                You must be at least 16 years old to opt in to our SMS program. By opting in, you confirm that
                you are at least 16 years of age and are authorized to use the mobile phone number you provided.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">13. Changes to This Policy</h2>
              <p className="mb-4">
                We may update this SMS Opt-In Policy from time to time to reflect changes in our practices or for
                legal, operational, or regulatory reasons. We will notify you of material changes by:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Posting the updated policy on this page with a new "Last Updated" date</li>
                <li>Sending an SMS notification for significant changes affecting your rights</li>
                <li>Email notification (if we have your email address)</li>
              </ul>
              <p className="mb-4">
                Your continued participation in our SMS program after changes are posted constitutes acceptance
                of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">14. Dispute Resolution</h2>
              <p className="mb-4">
                Any disputes arising from our SMS program are subject to the dispute resolution provisions in
                our{' '}
                <a href="/policies/terms-of-use" className="text-brand-teal hover:text-brand-teal-hover">
                  Terms of Use
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">15. Contact Information</h2>
              <p className="mb-4">
                If you have questions about our SMS program or this SMS Opt-In Policy, please contact us:
              </p>
              <p className="mb-4">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                  support@chathtl.com
                </a><br />
                <strong>SMS Support:</strong> Text HELP to any message you receive from us<br />
                <strong>Website:</strong>{' '}
                <a href="https://chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                  chathtl.com
                </a>
              </p>
              <p className="mb-4">
                <strong>Mailing Address:</strong><br />
                ChatHTL<br />
                Attn: ChatHTL SMS Support<br />
                [Company Address]
              </p>
            </section>

            <section className="bg-neutral-light-gray dark:bg-neutral-dark-gray rounded-lg p-6 mt-8">
              <h2 className="text-2xl font-semibold mb-4">Quick Reference</h2>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold mb-2">To Stop Messages:</p>
                  <p>Text STOP, CANCEL, UNSUBSCRIBE, or END to any message</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">For Help:</p>
                  <p>Text HELP to any message or email{' '}
                    <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                      support@chathtl.com
                    </a>
                  </p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Message Frequency:</p>
                  <p>Varies based on account activity. Promotional messages: up to 4/month</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Rates:</p>
                  <p>Message and data rates may apply</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Support:</p>
                  <p>
                    <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">
                      support@chathtl.com
                    </a>
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};
